import { ImageType } from "@/types/baseTypes";
import { PostsGridElementType } from "../components/PostCard/PostCard";
import { WPClient } from "../wp/client/WPClient";
import { PostsGridModuleType } from "../modules/PostGrid/PostGrid.server";
import { requireDefined } from "@/utils/envs";

/* jscpd:ignore-start */
interface FetchPostsProps {
  page: number;
  postsPerPage: number;
  offset: number;
  locale: string | undefined;
  module: PostsGridModuleType;
  fallback_options: "header-img" | "teaser-text";
  fallback_image?: ImageType | false;
  abortController?: AbortController;
  categoryOverride?: number[];
  excludeIds?: number[];
}
/* jscpd:ignore-end */

export const fetchPosts = async ({
  module,
  categoryOverride,
  ...props
}: FetchPostsProps) => {
  const { type, categories, posts: selectedPosts } = module.grid;

  return await fetchPostGridElements({
    ...props,
    type: categoryOverride ? "categories" : type,
    selectedPosts,
    categories: categoryOverride || categories,
  });
};

interface FetchPostGridElementsProps extends Omit<
  FetchPostsProps,
  "module" | "page" | "postsPerPage" | "offset"
> {
  type: "categories" | "posts" | "show-all" | "show-random";
  selectedPosts: "" | number[] | null;
  categories: number[] | false;
  limit?: number;
  page?: number;
  postsPerPage?: number;
  offset?: number;
  excludeIds?: number[];
}

export const fetchPostGridElements = async ({
  page,
  postsPerPage,
  offset,
  locale,
  fallback_options,
  abortController,
  fallback_image,
  type,
  selectedPosts,
  categories,
  limit,
  excludeIds,
}: FetchPostGridElementsProps) => {
  const defaultLocale = requireDefined(process.env.NEXT_PUBLIC_DEFAULT_LOCALE);
  const client = new WPClient({ locale, defaultLocale });
  const timestamp = Date.now();
  const postsResult = await client.getPosts({
    type: "blog",
    page: page,
    perPage: postsPerPage,
    offset,
    fields: [
      "id",
      "date_gmt",
      "_embedded",
      "acf.header_post.title",
      "acf.header_post.image",
      "acf.header_post.media",
      "link",
      "acf.header_post.excerpt",
      "categories",
    ],
    ...(type === "posts" && {
      includeIds: { ids: selectedPosts || [], keepOrder: true },
    }),
    ...(type === "categories" && {
      includeCategories: [{ category: "categories", ids: categories || [] }],
    }),
    excludeIds,
    abortController,
    ...(limit && { limit }),
    ...(type === "show-random" && {
      customParams: {
        orderby: "rand",
        _ts: timestamp.toString(),
      },
    }),
  });

  const posts = postsResult.posts.map((post) => {
    const header = post.acf?.header_post;
    const _categories = post._embedded?.["wp:term"]
      ? post._embedded["wp:term"][0]
      : [];

    return {
      id: post.id,
      date: post.date_gmt,
      title: header?.title || "",
      image:
        header?.image ||
        (fallback_options === "header-img"
          ? header.media.image_desktop
            ? header.media.image_desktop
            : fallback_image
              ? fallback_image
              : false
          : false),
      excerpt: header?.excerpt || "",
      link: post.link,
      categories: _categories ? _categories : [],
    } as PostsGridElementType;
  });
  return {
    posts,
    totalPosts: postsResult.totalPosts,
  };
};
